<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import JIcon from '@/components/atoms/JIcon.vue'
import JAvatar from '@/components/atoms/JAvatar.vue'
import JButton from '@/components/atoms/JButton.vue'
import JPopover from '@/components/atoms/JPopover.vue'
import { cn } from '@/lib/utils'
import type { ContextMenuItem, ContextMenuGroup } from '@/types/context-menu.types'
import {
  detectThemeClasses,
  ensureDefaultTheme,
  validateThemeExists,
  getDefaultTheme,
  getStoredTheme,
  setStoredTheme,
  applyTheme,
} from '@/lib/theme-utils'

// Fallback 로고 이미지 import (logo prop이 없거나 로드 실패 시 사용)
import logoFallback from '@/assets/images/logo-fallback.png'

/**
 * JHeader - 상단 헤더 컴포넌트 (organisms)
 * Header Component
 * 
 * @description
 * 애플리케이션의 상단 헤더 영역을 담당하는 컴포넌트입니다.
 * 로고, 네비게이션, 검색, 알림, 사용자 메뉴 등을 포함할 수 있습니다.
 * 
 * @example
 * ```vue
 * <JHeader
 *   logo="/logo.png"
 *   :user-menu-items="userMenuItems"
 *   @logo-click="handleLogoClick"
 * />
 * ```
 */

export type HeaderNavItem = {
  /** 라벨 */
  label: string
  /** 링크 URL */
  href?: string
  /** 아이콘 */
  icon?: string
  /** 활성 상태 */
  active?: boolean
  /** 클릭 핸들러 */
  onClick?: () => void
}

export type NotificationItem = {
  /** 알림 ID */
  id: string
  /** 알림 제목 */
  title: string
  /** 알림 내용 */
  message?: string
  /** 알림 시간 */
  time?: string
  /** 읽음 상태 */
  read?: boolean
  /** 아이콘 */
  icon?: string
  /** 클릭 핸들러 */
  onClick?: () => void
}

type StyleType =
  | 'default'   // 기본 스타일
  | 'minimal'   // 최소 스타일

const props = withDefaults(
  defineProps<{
    /** 로고 이미지 URL */
    logo?: string
    /** 로고 텍스트 (기본값, 이미지가 없을 때) */
    logoText?: string
    /** 네비게이션 아이템 목록 */
    navItems?: HeaderNavItem[]
    /** 알림 표시 여부 */
    showNotifications?: boolean
    /** 알림 목록 */
    notifications?: NotificationItem[]
    /** 사용자 아바타 이미지 */
    userAvatar?: string
    /** 사용자 이름 */
    userName?: string
    /** 로그인 상태 */
    isLoggedIn?: boolean
    /** 사용자 이메일 */
    userEmail?: string
    /** 사용자 ID */
    userId?: string
    /** 스타일 타입 */
    styletype?: StyleType
    /** 사이드바 토글 버튼 표시 여부 */
    showSidebarToggle?: boolean
    /** 사이드바 열림 상태 */
    isSidebarOpen?: boolean
    /** 테마 선택기 표시 여부 */
    showThemeSelector?: boolean
    /** 초기 테마 (기본값: 'default' 또는 저장된 테마) */
    defaultTheme?: string
    /** 선택 가능한 테마 목록 (지정하지 않으면 모든 감지된 테마 사용) */
    availableThemes?: string[]
  }>(),
  {
    logoText: 'JWMS Portal', // 기본값: 텍스트 로고
    showNotifications: false,
    notifications: () => [],
    styletype: 'default',
    showSidebarToggle: true,
    isSidebarOpen: true,
    showThemeSelector: true,
    defaultTheme: undefined,
    availableThemes: undefined,
  }
)

const emit = defineEmits<{
  /** 로고 클릭 이벤트 */
  logoClick: []
  /** 네비게이션 아이템 클릭 이벤트 */
  navClick: [item: HeaderNavItem, index: number]
  /** 알림 클릭 이벤트 */
  notificationClick: [item: NotificationItem]
  /** 사용자 메뉴 아이템 선택 이벤트 */
  userMenuSelect: [itemId: string]
  /** 사이드바 토글 이벤트 */
  sidebarToggle: []
  /** 로그인 버튼 클릭 이벤트 */
  login: []
}>()

/**
 * 스타일 프리셋
 */
const STYLE_PRESETS: Record<StyleType, {
  containerClass: string
  navItemClass: string
  navItemActiveClass: string
}> = {
  default: {
    containerClass: 'h-10 px-4 border-b border-border bg-muted dark:bg-card',
    navItemClass: 'text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-md hover:bg-accent',
    navItemActiveClass: 'text-foreground font-medium bg-accent',
  },
  minimal: {
    containerClass: 'h-8 px-3 border-b border-border bg-muted dark:bg-card',
    navItemClass: 'text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-accent',
    navItemActiveClass: 'text-foreground font-medium bg-accent',
  },
}

const preset = computed(() => {
  return STYLE_PRESETS[props.styletype] ?? STYLE_PRESETS.default
})

/**
 * 로고 이미지 로드 에러 상태
 * 0: 에러 없음, 1: 첫 번째 이미지(logo) 에러, 2: fallback 이미지도 에러
 */
const logoImageError = ref(0)

/**
 * 로고 이미지 경로 계산
 */
const logoImageSrc = computed(() => {
  // props.logo가 있으면 사용
  if (props.logo) {
    return props.logo
  }
  return undefined
})

/**
 * 실제 표시할 로고 이미지 경로
 * 1. props.logo가 있으면 사용
 * 2. logo 에러 시 fallback 이미지
 * 3. fallback도 에러면 undefined (텍스트로 대체)
 */
const displayLogoImage = computed(() => {
  // logo prop이 없으면 이미지 표시 안함 (텍스트 사용)
  if (!props.logo) {
    return undefined
  }
  
  // logo 에러가 발생했으면 fallback 사용
  if (logoImageError.value >= 1 && logoFallback) {
    return logoFallback
  }
  
  // 정상적으로 logo 사용
  return logoImageSrc.value
})

/**
 * 로고 이미지 로드 에러 핸들러
 */
const handleLogoError = () => {
  // 첫 번째 이미지(logo) 에러
  if (logoImageError.value === 0) {
    logoImageError.value = 1
  }
  // fallback 이미지도 에러면 2로 설정 (텍스트로 대체)
  else if (logoImageError.value === 1) {
    logoImageError.value = 2
  }
}

/**
 * 읽지 않은 알림 개수
 */
const unreadCount = computed(() => {
  return props.notifications?.filter(n => !n.read).length || 0
})

/**
 * 고정 사용자 메뉴 아이템
 */
const fixedUserMenuItems: ContextMenuItem[] = [
  { id: 'profile', label: '프로필', icon: 'user' },
  { id: 'settings', label: '설정', icon: 'settings' },
  { id: 'separator', label: '', separator: true },
  { id: 'logout', label: '로그아웃', icon: 'logOut' },
]

/**
 * 사용자 메뉴 아이템을 ContextMenuGroup 형식으로 변환
 */
const userMenuGroups = computed<ContextMenuGroup[]>(() => {
  return [{
    items: fixedUserMenuItems
  }]
})

/**
 * 로고 클릭 핸들러
 */
const handleLogoClick = () => {
  emit('logoClick')
}

/**
 * 네비게이션 아이템 클릭 핸들러
 */
const handleNavClick = (item: HeaderNavItem, index: number) => {
  item.onClick?.()
  emit('navClick', item, index)
}

/**
 * 알림 클릭 핸들러
 */
const handleNotificationClick = (item: NotificationItem) => {
  item.onClick?.()
  emit('notificationClick', item)
}

/**
 * 사용자 메뉴 선택 핸들러
 */
const handleUserMenuSelect = (itemId: string) => {
  emit('userMenuSelect', itemId)
}

/**
 * 로그인 버튼 클릭 핸들러
 */
const handleLogin = () => {
  emit('login')
}

/**
 * 사이드바 토글 핸들러
 */
const handleSidebarToggle = () => {
  emit('sidebarToggle')
}

/**
 * tweakcn 테마 타입 정의 (동적 감지 지원)
 */
type ThemeName = string

/**
 * 다크모드 상태를 추적하는 반응형 변수
 * - false: 라이트 모드
 * - true: 다크 모드
 * 초기값은 false(라이트 모드)이며, 컴포넌트 마운트 시 실제 테마로 업데이트됩니다.
 */
const isDarkMode = ref(false)

/**
 * 현재 선택된 tweakcn 테마 (디폴트 테마로 초기화)
 */
const currentTheme = ref<ThemeName>(getDefaultTheme())

/**
 * 테마 옵션 목록 (동적으로 감지)
 */
const themeOptions = ref<ThemeName[]>([])

/**
 * 내부 업데이트 플래그
 * - true: 컴포넌트 내부에서 테마를 변경 중 (MutationObserver가 무시해야 함)
 * - false: 외부에서 테마가 변경됨 (MutationObserver가 동기화해야 함)
 */
let isInternalUpdate = false

/**
 * MutationObserver 인스턴스
 */
let themeObserver: MutationObserver | null = null
let styleObserver: MutationObserver | null = null

/**
 * Storybook 환경인지 확인하는 함수
 * 
 * @returns Storybook 환경이면 true, 아니면 false
 */
const isStorybook = (): boolean => {
  if (typeof window === 'undefined') return false
  return !!(
    window.location?.href?.includes('storybook') ||
    (window as any).__STORYBOOK_GLOBALS__
  )
}

/**
 * 테마 목록을 동적으로 감지하고 업데이트합니다.
 */
const updateThemeOptions = () => {
  const detectedThemes = detectThemeClasses()
  const ensuredThemes = ensureDefaultTheme(detectedThemes)
  
  // availableThemes prop이 지정되면 필터링
  if (props.availableThemes && props.availableThemes.length > 0) {
    const filtered = ensuredThemes.filter(theme => props.availableThemes!.includes(theme))
    // default는 항상 포함
    themeOptions.value = ensureDefaultTheme(filtered)
  } else {
    themeOptions.value = ensuredThemes
  }
}

/**
 * localStorage에서 저장된 다크모드 테마를 읽어오는 함수
 * 
 * 우선순위:
 * 1. localStorage에 저장된 'theme' 값 (사용자가 이전에 선택한 테마)
 * 2. 시스템 설정 (OS의 다크모드 설정)
 * 3. 기본값 'light' (라이트 모드)
 * 
 * @returns 'light' | 'dark' - 적용할 테마
 */
const getStoredDarkModeTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light'
  
  const stored = localStorage.getItem('theme')
  if (stored === 'dark' || stored === 'light') {
    return stored
  }
  
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark'
  }
  
  return 'light'
}

/**
 * 다크모드 테마를 HTML 문서에 적용하는 함수
 * 
 * @param theme - 적용할 테마 ('light' 또는 'dark')
 */
const applyDarkModeTheme = (theme: 'light' | 'dark') => {
  isInternalUpdate = true
  
  const root = document.documentElement
  
  if (theme === 'dark') {
    root.classList.add('dark')
    isDarkMode.value = true
  } else {
    root.classList.remove('dark')
    isDarkMode.value = false
  }
  
  localStorage.setItem('theme', theme)
  
  isInternalUpdate = false
}

/**
 * 테마 토글 버튼 클릭 시 호출되는 핸들러 함수
 */
const handleThemeToggle = () => {
  const newTheme = isDarkMode.value ? 'light' : 'dark'
  applyDarkModeTheme(newTheme)
}

/**
 * 저장된 tweakcn 테마를 가져와서 적용합니다.
 */
const getStoredTweakcnTheme = (): ThemeName => {
  // defaultTheme prop이 지정되면 우선 사용
  if (props.defaultTheme) {
    const validated = validateThemeExists(props.defaultTheme)
    return validated
  }
  
  const stored = getStoredTheme('tweakcn-theme')
  if (!stored) {
    return getDefaultTheme()
  }
  
  // 저장된 테마가 유효한지 검증
  const validated = validateThemeExists(stored)
  return validated
}

/**
 * tweakcn 테마를 HTML 문서에 적용하는 함수
 */
const applyTweakcnTheme = (theme: ThemeName): boolean => {
  isInternalUpdate = true
  
  const validatedTheme = validateThemeExists(theme)
  const success = applyTheme(validatedTheme)
  
  if (success) {
    currentTheme.value = validatedTheme
    
    // Storybook 환경이 아닐 때만 localStorage에 저장
    // Storybook에서는 툴바가 source of truth이므로 JHeader 변경을 무시
    if (!isStorybook()) {
      setStoredTheme(validatedTheme, 'tweakcn-theme')
    }
  } else {
    // 실패 시 디폴트 테마로 폴백
    const defaultTheme = getDefaultTheme()
    applyTheme(defaultTheme)
    currentTheme.value = defaultTheme
    setStoredTheme(defaultTheme, 'tweakcn-theme')
  }
  
  isInternalUpdate = false
  return success
}

/**
 * 테마 변경 핸들러
 */
const handleThemeChange = (theme: ThemeName | string | number) => {
  applyTweakcnTheme(String(theme))
}

/**
 * 현재 테마에 따라 표시할 아이콘을 계산하는 computed 속성
 */
const themeIcon = computed(() => {
  return isDarkMode.value ? 'sun' : 'moon'
})

/**
 * 초기화: 저장된 테마 적용 및 테마 목록 감지
 */
onMounted(() => {
  // 테마 목록 초기 감지
  updateThemeOptions()
  
  // 저장된 다크모드 테마 적용
  const darkModeTheme = getStoredDarkModeTheme()
  applyDarkModeTheme(darkModeTheme)
  
  // 저장된 tweakcn 테마 적용
  const storedTheme = getStoredTweakcnTheme()
  applyTweakcnTheme(storedTheme)
  
  // HTML의 dark 클래스 변경을 감지하여 헤더 컴포넌트 상태를 동기화
  const root = document.documentElement
  
  // 현재 dark 클래스 상태로 초기화
  isDarkMode.value = root.classList.contains('dark')
  
  // MutationObserver로 동적 테마 클래스 감지
  themeObserver = new MutationObserver(() => {
    if (isInternalUpdate) return
    
    // 다크모드 클래스 변경 감지
    const hasDarkClass = root.classList.contains('dark')
    if (hasDarkClass !== isDarkMode.value) {
      isDarkMode.value = hasDarkClass
      localStorage.setItem('theme', hasDarkClass ? 'dark' : 'light')
    }
    
    // 테마 목록 업데이트
    updateThemeOptions()
    
    // 현재 적용된 tweakcn 테마 확인
    const currentThemeClass = Array.from(root.classList).find(cls => cls.startsWith('theme-'))
    if (currentThemeClass) {
      const themeName = currentThemeClass.replace('theme-', '')
      if (themeOptions.value.includes(themeName)) {
        currentTheme.value = themeName
      }
    }
  })
  
  // document.documentElement의 클래스 변경 감지
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })
  
  // 스타일시트 변경 감지 (동적으로 추가된 테마 감지)
  styleObserver = new MutationObserver(() => {
    updateThemeOptions()
  })
  
  styleObserver.observe(document.head, {
    childList: true,
    subtree: true,
  })
  
  // 스토리북 환경에서만 다크모드 및 테마 변경 감지
  // Storybook 툴바가 source of truth이며, JHeader는 이를 따라감
  let intervalId: ReturnType<typeof setInterval> | null = null
  
  if (isStorybook()) {
    const checkStorybookGlobals = () => {
      try {
        const storybookGlobals = (window as any).__STORYBOOK_GLOBALS__
        if (storybookGlobals) {
          // 다크모드 동기화 (Storybook → JHeader)
          if (typeof storybookGlobals.darkMode !== 'undefined') {
            const storybookDarkMode = storybookGlobals.darkMode
            if (storybookDarkMode !== isDarkMode.value) {
              // isInternalUpdate를 사용하지 않음 - Storybook이 source of truth
              const root = document.documentElement
              if (storybookDarkMode) {
                root.classList.add('dark')
                isDarkMode.value = true
              } else {
                root.classList.remove('dark')
                isDarkMode.value = false
              }
              // Storybook 환경에서는 localStorage 업데이트 안 함
            }
          }
          
          // tweakcn 테마 동기화 (Storybook → JHeader)
          if (storybookGlobals.theme && storybookGlobals.theme !== currentTheme.value) {
            const storybookTheme = String(storybookGlobals.theme)
            if (themeOptions.value.includes(storybookTheme)) {
              // isInternalUpdate를 사용하지 않음 - Storybook이 source of truth
              const validatedTheme = validateThemeExists(storybookTheme)
              applyTheme(validatedTheme)
              currentTheme.value = validatedTheme
              // Storybook 환경에서는 localStorage 업데이트 안 함
            }
          }
        }
      } catch {
        // 무시
      }
    }

    // 초기 확인
    checkStorybookGlobals()
    
    // 주기적으로 확인 (200ms마다 - 더 빠른 동기화)
    intervalId = setInterval(checkStorybookGlobals, 200)
  }
  
  // 컴포넌트 언마운트 시 정리
  onUnmounted(() => {
    if (themeObserver) {
      themeObserver.disconnect()
      themeObserver = null
    }
    if (styleObserver) {
      styleObserver.disconnect()
      styleObserver = null
    }
    if (intervalId) {
      clearInterval(intervalId)
    }
  })
})
</script>

<template>
  <header :class="cn('flex items-center justify-between w-full', preset.containerClass)">
    <!-- 왼쪽: 햄버거 메뉴 + 로고 + 네비게이션 -->
    <div class="flex items-center gap-6 flex-1">
      <!-- 햄버거 메뉴 버튼 (사이드바 토글) -->
      <JButton
        v-if="showSidebarToggle"
        variant="ghost"
        size="sm"
        class="flex-shrink-0 w-8 h-8 p-0"
        aria-label="사이드바 토글"
        @click="handleSidebarToggle"
      >
        <JIcon name="menu" size="sm" />
      </JButton>

      <!-- 로고 -->
      <div
        v-if="displayLogoImage || logoText"
        class="flex items-center cursor-pointer"
        @click="handleLogoClick"
      >
        <!-- 로고 이미지 (logo prop이 있고 에러가 2 미만일 때) -->
        <img
          v-if="displayLogoImage && logoImageError < 2"
          :src="displayLogoImage"
          alt="로고"
          class="h-6 w-auto"
          @error="handleLogoError"
        />
        <!-- 로고 텍스트 (기본 또는 모든 이미지 실패 시) -->
        <span
          v-else-if="logoText"
          class="text-sm font-bold text-foreground"
        >
          {{ logoText }}
        </span>
      </div>

      <!-- 네비게이션 -->
      <nav
        v-if="navItems && navItems.length > 0"
        class="flex items-center gap-1"
      >
        <JButton
          v-for="(item, index) in navItems"
          :key="index"
          variant="ghost"
          :class="cn(
            preset.navItemClass,
            item.active && preset.navItemActiveClass
          )"
          @click="handleNavClick(item, index)"
        >
          <JIcon
            v-if="item.icon"
            :name="item.icon"
            size="sm"
            class="mr-1.5"
          />
          {{ item.label }}
        </JButton>
      </nav>
    </div>

    <!-- 오른쪽: 테마 선택기 + 알림 + 다크모드 토글 + 사용자 메뉴 -->
    <div class="flex items-center gap-3">
      <!-- tweakcn 테마 선택기 -->
      <JPopover
        v-if="showThemeSelector"
        position="bottom"
        align="end"
        styletype="default-sm"
      >
        <template #trigger>
          <JButton
            variant="ghost"
            size="sm"
            class="w-8 h-8 p-0"
            aria-label="테마 선택"
          >
            <JIcon name="palette" size="sm" />
          </JButton>
        </template>
        <div class="p-2 min-w-[200px]">
          <div class="text-xs font-medium text-muted-foreground px-2 py-1.5 mb-1">
            테마 선택
          </div>
          <div class="space-y-1">
            <button
              v-for="theme in themeOptions"
              :key="theme"
              :class="cn(
                'w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-md transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                'border-0 outline-none text-left',
                currentTheme === theme && 'bg-accent text-accent-foreground font-medium'
              )"
              @click="handleThemeChange(theme)"
            >
              <JIcon
                v-if="currentTheme === theme"
                name="check"
                size="sm"
                class="flex-shrink-0"
              />
              <span v-else class="w-4" />
              <span class="flex-1 capitalize">{{ theme }}</span>
            </button>
          </div>
        </div>
      </JPopover>

      <!-- 알림 -->
      <JPopover
        v-if="showNotifications"
        position="bottom"
        align="end"
        styletype="default-sm"
      >
        <template #trigger>
          <JButton
            variant="ghost"
            size="sm"
            class="relative w-8 h-8 p-0"
            aria-label="알림"
          >
            <JIcon name="circleAlert" size="sm" />
            <span
              v-if="unreadCount > 0"
              class="absolute top-0 right-0 h-4 w-4 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center"
            >
              {{ unreadCount > 9 ? '9+' : unreadCount }}
            </span>
          </JButton>
        </template>
        <div class="p-2">
          <div
            v-if="notifications && notifications.length > 0"
            class="max-h-96 overflow-y-auto space-y-1"
          >
            <div
              v-for="notification in notifications"
              :key="notification.id"
              :class="cn(
                'p-3 rounded-md cursor-pointer transition-colors',
                !notification.read ? 'bg-accent' : 'hover:bg-accent/50'
              )"
              @click="handleNotificationClick(notification)"
            >
              <div class="flex items-start gap-2">
                <JIcon
                  v-if="notification.icon"
                  :name="notification.icon"
                  size="sm"
                  class="mt-0.5 flex-shrink-0"
                />
                <div class="flex-1 min-w-0">
                  <p class="text-xs font-medium text-foreground">
                    {{ notification.title }}
                  </p>
                  <p
                    v-if="notification.message"
                    class="text-xs text-muted-foreground mt-1"
                  >
                    {{ notification.message }}
                  </p>
                  <p
                    v-if="notification.time"
                    class="text-xs text-muted-foreground/60 mt-1"
                  >
                    {{ notification.time }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div
            v-else
            class="p-4 text-center text-xs text-muted-foreground"
          >
            알림이 없습니다.
          </div>
        </div>
      </JPopover>

      <!-- 다크모드 토글 버튼 -->
      <JButton
        variant="ghost"
        size="sm"
        class="w-8 h-8 p-0"
        :aria-label="isDarkMode ? '라이트 모드로 전환' : '다크 모드로 전환'"
        @click="handleThemeToggle"
      >
        <JIcon :name="themeIcon" size="sm" />
      </JButton>

      <!-- 커스텀 툴바 슬롯 (다크모드 토글 버튼 우측) -->
      <slot name="toolbar" />

      <!-- 사용자 메뉴 (userName이 있으면 표시) -->
      <JPopover
        v-if="userName"
        position="bottom"
        align="end"
        styletype="default-sm"
      >
        <template #trigger>
          <div class="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
            <JAvatar
              :src="userAvatar"
              :fallback="userName ? userName[0] : 'U'"
              size="xs"
            />
            <span
              class="text-xs text-foreground hidden sm:inline"
            >
              {{ userName }}
            </span>
            <JIcon name="chevronDown" size="sm" class="text-muted-foreground hidden sm:inline" />
          </div>
        </template>
        <div class="w-full rounded-md overflow-hidden">
          <!-- 프로필 정보 영역 -->
          <div class="px-3 py-1.5 border-b border-border">
            <p class="text-xs font-medium text-foreground">{{ userName }}</p>
            <p
              v-if="userEmail"
              class="text-xs text-muted-foreground mt-0.5"
            >
              {{ userEmail }}
            </p>
          </div>
          
          <!-- 메뉴 아이템 -->
          <template v-for="(group, groupIndex) in userMenuGroups" :key="groupIndex">
            <template v-for="(item, itemIndex) in group.items" :key="item.id">
              <button
                v-if="!item.separator"
                :disabled="item.disabled"
                :class="cn(
                  'w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors',
                  item.id === 'logout' 
                    ? 'text-destructive hover:bg-destructive/10 hover:text-destructive' 
                    : 'hover:bg-accent hover:text-accent-foreground',
                  'disabled:opacity-50 disabled:pointer-events-none',
                  'border-0 outline-none',
                  group.items[itemIndex + 1]?.separator && 'border-b-0'
                )"
                @click="handleUserMenuSelect(item.id)"
              >
                <JIcon
                  v-if="item.icon"
                  :name="item.icon"
                  size="sm"
                  :class="cn(
                    'flex-shrink-0',
                    item.id === 'logout' && 'text-destructive'
                  )"
                />
                <span class="flex-1 text-left truncate">{{ item.label }}</span>
              </button>
              <div
                v-else-if="item.separator && itemIndex > 0"
                class="h-px bg-muted my-1"
              />
            </template>
            <div
              v-if="groupIndex < userMenuGroups.length - 1"
              class="h-px bg-muted my-1"
            />
          </template>
        </div>
      </JPopover>

      <!-- 로그인 버튼 (userName이 없으면 표시) -->
      <JButton
        v-else
        variant="ghost"
        size="sm"
        aria-label="로그인"
        @click="handleLogin"
      >
        <JIcon name="logIn" size="sm" class="sm:mr-1.5" />
        <span class="hidden sm:inline">로그인</span>
      </JButton>
    </div>
  </header>
</template>
